import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Badge,
    Alert,
} from "reactstrap";
import { ACCOUNT_TYPE_LABEL_MAP } from "../../../constants/accountTypes";

/**
 * AccountDeleteModal – Cari silme onayı
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen      – Modal açık mı?
 * @param {Function} toggle      – Modal'ı kapat/aç
 * @param {Object}   account     – Silinecek cari bilgileri
 * @param {Function} onConfirm   – Silme onaylandığında çalışır
 */
const AccountDeleteModal = ({ isOpen, toggle, account, onConfirm }) => {
    const [deleting, setDeleting] = useState(false);

    if (!account) return null;

    /* ─────────────────────────────────────────────────────────
     *  Yardımcı Fonksiyonlar
     * ───────────────────────────────────────────────────────── */

    const getAccountTypeBadgeColor = (accountType) => {
        switch (accountType) {
            case "CUSTOMER":
                return "success";
            case "SUPPLIER":
                return "info";
            case "BOTH":
                return "primary";
            case "SAHIS_SIRKETI":
                return "warning";
            default:
                return "secondary";
        }
    };

    const translateAccountType = (accountType) =>
        ACCOUNT_TYPE_LABEL_MAP[accountType] || accountType;

    const handleConfirm = async () => {
        if (!onConfirm || !account.id) return;

        setDeleting(true);
        try {
            await onConfirm(account.id);
        } catch (error) {
            console.error("Delete error:", error);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-danger">
                    <i className="bx bx-trash fs-4"></i>
                    Cari/Bayi Sil
                </div>
            </ModalHeader>

            <ModalBody className="py-4">

                <div className="text-center mb-4">
                    <div className="mb-3">
                        <i className="bx bx-building display-4 text-muted"></i>
                    </div>
                    <p className="text-muted mb-2">Aşağıdaki cari/bayi hesabı silmek istediğinizden emin misiniz?</p>
                </div>

                {/* Cari Bilgileri Özeti */}
                <div className="border rounded p-3 bg-light">
                    <div className="row">
                        <div className="col-12 mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="mb-1 text-dark">{account.accountName}</h6>
                                    <div className="text-muted small">
                                        Cari Kodu: <strong>{account.accountCode}</strong>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <Badge color={getAccountTypeBadgeColor(account.accountType)}>
                                        {translateAccountType(account.accountType)}
                                    </Badge>
                                    <br />
                                    <Badge
                                        color={account.enabled ? "success" : "danger"}
                                        className="mt-1"
                                    >
                                        {account.enabled ? "Aktif" : "Pasif"}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {(account.email || account.phoneNumber) && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-muted small">
                                    {account.email && (
                                        <div>
                                            <i className="bx bx-envelope me-1"></i>
                                            {account.email}
                                        </div>
                                    )}
                                    {account.phoneNumber && (
                                        <div>
                                            <i className="bx bx-phone me-1"></i>
                                            {account.phoneNumber}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {account.companies && account.companies.length > 0 && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-muted small mb-1">Bağlı Firmalar:</div>
                                <div className="d-flex flex-wrap gap-1">
                                    {account.companies.slice(0, 3).map((company, index) => (
                                        <Badge
                                            key={company.id || index}
                                            color="outline-secondary"
                                            className="small"
                                        >
                                            {company.companyName || company.name}
                                        </Badge>
                                    ))}
                                    {account.companies.length > 3 && (
                                        <Badge color="outline-secondary" className="small">
                                            +{account.companies.length - 3} diğer
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        color="secondary"
                        onClick={toggle}
                        disabled={deleting}
                        className="rounded-pill px-4"
                    >
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleConfirm}
                        disabled={deleting}
                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" />
                                Siliniyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-trash"></i>
                                Evet, Sil
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default AccountDeleteModal;